import ReplayOutlinedIcon from "@mui/icons-material/ReplayOutlined";
import EnvironmentsStore from "csi/state/3D/environmentsStore";
import { useStore } from "csi/state/hooks";
import RightPaneStore from "csi/state/rightPane";
import rootStore from "csi/state/rootStore";
import DeleteDialog from "designSystem/components/dialog/DeleteDialog";
import ErrorDialog from "designSystem/components/dialog/ErrorDialog";
import { Button, Chip, ConfirmDialog, CsiImage, FlexColumn, FlexRow, FormControlLabel, GridRow, ListItem, MenuItem, MoreHorizMenu, OverflowText, Radio, TextField } from "designSystem/index";
import { ILocaleProvider, useLocale } from "designSystem/locale";
import { action, computed, makeAutoObservable, observable } from "mobx";
import { observer, useLocalObservable } from "mobx-react-lite";
import React, { FunctionComponent } from "react";
import { C8Node } from "src/c8-platform/type/node";
import { getNodeAttributeImageSrc } from "src/csi/model/image";

interface EnvironmentItemProps {
    checked: boolean;
    item: C8Node;
}

interface MenuOptions {
	onClick: (event: React.MouseEvent) => void;
	res: string;
}


type SaveEnvironmentName = (url: string, name: string) => void;

const ALIGN_BOTTOM = {
	"&.MuiButton-root": {
		alignItems: "flex-end",
	},
};

const WIDTH = {
	"& .MuiGrid-item:last-child ": {
		width: "100%",
	},
};

const LIST_STYLES = {
	"& .moreHorzMenuIcon": {
		display: "none",
		transform: "rotateZ(90deg)",
	},
	"&.MuiListItem-root": {
		padding: 0,
		marginTop: 0,
	},
	"&.MuiListItem-root>.MuiBox-root": {
		overflow: "hidden",
	},
	"&:hover": {
		"& .moreHorzMenuIcon": {
			display: "inline-flex",
		},
	},
	"& .MuiFormControlLabel-root>div:first-of-type": {
		marginRight: 3,
	},
	"& .textEdit+.moreHorzMenuIcon": {
		display: "none",
	},
};

class LocalEnvironmentItemStore {
	/** determine if item is checked */
	checked: boolean;

	/** Environment component item */
	item: C8Node;

	/** Save environment name callback */
	saveEnvironmentName: SaveEnvironmentName;

	/** Environment store */
	@observable.ref environmentStore: EnvironmentsStore;

	constructor(checked: boolean, item: C8Node, saveEnvironmentName: SaveEnvironmentName, environmentStore: EnvironmentsStore){
		makeAutoObservable(this);
		this.checked = checked;
		this.item = item;
		this.saveEnvironmentName = saveEnvironmentName;
		this.environmentStore = environmentStore;
	}

	/** Determine if the name is in an editing state */
	@observable isEditingName = false;

	/** return image src for component item */
	get imageSrc() {
		return getNodeAttributeImageSrc(this.item, "EnvironmentImage");
	}

	/** Determine if the item is the current default */
	@computed get isDefaultEnvironment(): boolean {
		return this.item.$URL === this.environmentStore.defaultEnvironmentURL;
	}

	/** Set name edit state */
	@action setIsEditingName = (isEditing: boolean) => {
		this.isEditingName = isEditing;
	};
		
	/** callback for name text on focus handle */
	@action nameTextFieldOnFocus = (event: React.FocusEvent<HTMLInputElement>): void => {
		event.stopPropagation();
	};

	/** Blur item callback */
	blurItemIfNotChecked = (event: React.FocusEvent<HTMLLIElement>): void => {
		// If the More sub menu is clicked, return from this action
		if (event.target.classList.contains("moreHorzMenuIcon")) {
			return;
		}

		// Blur the list item if the item that is focus is not the one that is checked and if this action did not come from a keyboard event
		// The event.relatedTarget is null when the focus is set by mouse click         
		if (!this.checked && !event.relatedTarget) {
			event.target.blur();
			event.stopPropagation();
		}
	};

	/** Save name text field */
	@action saveNameTextField = (event: React.KeyboardEvent<HTMLInputElement>): void => {
		event.stopPropagation();
		this.setIsEditingName(false);

		if (event.target instanceof HTMLInputElement) {
			this.saveEnvironmentName(this.item.$URL, event.target.value);
		}
	};

	/** callback for name text on blur handle */
	@action nameTextFieldOnBlur = (event: React.FocusEvent<HTMLInputElement>): void => {
		event.stopPropagation();
		this.setIsEditingName(false);

		if (event.target instanceof HTMLInputElement) {
			this.saveEnvironmentName(this.item.$URL, event.target.value);
		}
	};


    /** Delete the current environment item */
    @action onDelete = (loc: ILocaleProvider, rightPaneStore: RightPaneStore): void => {
    	rightPaneStore.openDialog(DeleteDialog, {
    		nodeName: loc.getNodeName(this.item),
    		typeName: loc.type("ThreeDEnvironment"),
    		onConfirm: async () => {
    			try {
    				await this.environmentStore.deleteEnvironment(this.item.$URL);
    			} catch (error) {
    				rightPaneStore.openDialog(ErrorDialog, {
    					error,
    					onClose: this.environmentStore.closeEnvironmentsErrorDialog,
    				});
    			}
    		},
    		showClose: true,
    	});
    };

    /** Get menu options for the current environment item */
    getMenuOptions(loc: ILocaleProvider, rightPaneStore: RightPaneStore): MenuOptions[] {
    	const options: MenuOptions[] = [];
    	const { isDefaultEnvironment, item, setIsEditingName } = this;

    	if (this.environmentStore.centricStudioEnvironmentURL !== item.$URL) {
    		if (!isDefaultEnvironment) {
    			options.push({
    				onClick: (event: React.MouseEvent) => this.onDelete(loc, rightPaneStore),
    				res: "Delete",
    			});
    		}
    		options.push({
    			onClick: (event: React.MouseEvent) => setIsEditingName(true),
    			res: "Rename",
    		});
    	}

    	if (!isDefaultEnvironment) {
    		options.push({
    			onClick: (event: React.MouseEvent) => this.environmentStore.toggleEnvironmentAsDefault(item.$URL),
    			res: "SetAsDefault",
    		});
    	}

    	return options;
    }
}

const LocalEnvironmentItemStoreContext = React.createContext<LocalEnvironmentItemStore>(null);
const useLocalEnvironmentItemStore = () => React.useContext(LocalEnvironmentItemStoreContext);

export const EnvironmentItem: FunctionComponent<EnvironmentItemProps> = observer((props) => {
	const {
		checked,
		item,
	} = props;
	const { rightPaneStore } = useStore();
	const loc = useLocale();
	const { threeDStore: { environmentsStore } } = rootStore;
	const {
		environmentCanReset,
		resetUserEnvironmentSettings,
		saveEnvironmentName,
	} = environmentsStore;
	
	const localStore = useLocalObservable(() => new LocalEnvironmentItemStore(checked, item, saveEnvironmentName, environmentsStore));

	const {
		isDefaultEnvironment, 
		isEditingName,
		imageSrc, 
		blurItemIfNotChecked, 
		nameTextFieldOnBlur, 
		nameTextFieldOnFocus, 
		saveNameTextField,
	} = localStore;

	const confirmEnvironmentReset = () => {
		rightPaneStore.openDialog(ConfirmDialog, {
			category: "error",
			confirmRes: "BUTTON_RESET",
			messageRes: "3DEnvironmentReset",
			onConfirm: resetUserEnvironmentSettings,
			showClose: true,
			titleRes: "ResetToOriginal",
		});
	};

	return (
		<LocalEnvironmentItemStoreContext.Provider value={localStore}>
			<ListItem
				design="list"
				disableGutters
				onFocus={blurItemIfNotChecked}
				selected={checked}
				sx={LIST_STYLES}
			>
				<GridRow
					flexWrap="nowrap"
					grow
					growChild={0}
					spacing={2}
					spacingAroundX={3}
					spacingAroundY={2}
					textAlign="left"
				>
					<FormControlLabel
						block
						control={<Radio />}
						label={(
							<GridRow
								flexWrap="nowrap"
								grow
								growChild={1}
								spacing={2}
								sx={WIDTH}
							>
								<CsiImage 
									aspectRatio={2}
									borderRadius={5}
									size="smallest"
									src={imageSrc}
									width={96}
								/>
								<FlexColumn alignItems="flex-start" justifyContent="space-between">
									<FlexRow justifyContent="space-between" width="100%">
										<FlexRow
											className={isEditingName ?  "textEdit" : ""}
											flexGrow={1}
											gap={1}
											width="84%"
										>
											{
												isEditingName ? (
													<TextField
														autoFocus
														defaultValue={loc.getNodeName(item)}
														onBlur={nameTextFieldOnBlur}
														onEnter={saveNameTextField}
														onFocus={nameTextFieldOnFocus}
														placeholder={loc.getNodeName(item)}
													/>
												) : (
													<OverflowText
														text={loc.getNodeName(item)}
													/>
												)
											}
										</FlexRow>
										<MoreMenu />
									</FlexRow>
									<FlexRow
										alignItems="center"
										width="100%"
									>
										{ isDefaultEnvironment && (
											<Chip
												res="Default"
												style={{ maxWidth: 80 }}
												variant="outlined"
											/>
										)}
										<Button
											design="link"
											onClick={confirmEnvironmentReset}
											res="Reset"
											resParams={["Button"]}
											startIcon={<ReplayOutlinedIcon />}
											sx={{
												...ALIGN_BOTTOM,
												visibility: environmentCanReset && checked ? "visible" : "hidden",
											}}
										/>
									</FlexRow>
								</FlexColumn>
							</GridRow>
						)}
						labelPlacement="end"
						labelStyle={{
							flexGrow: 1,
							width: "100%",
						}}
						value={item.$URL}
						wrap={false}
					/>
				</GridRow>
			</ListItem>
		</LocalEnvironmentItemStoreContext.Provider>
	);
});

const MoreMenu: FunctionComponent = observer((props) => {
	const { rightPaneStore } = useStore();
	const loc = useLocale();
	const { threeDStore: { has3DLibraryAdminRole } } = rootStore;
	const localStore = useLocalEnvironmentItemStore();

	if (!has3DLibraryAdminRole) {
		return null;
	}	
	
	const options = localStore.getMenuOptions(loc, rightPaneStore);

	if (options.length === 0) {
		return null;
	}	

	return (
		<MoreHorizMenu
			className='moreHorzMenuIcon'
			design="iconOnly"
			size="large"
		>
			{options.map((option, index) => (
				<MenuItem
					key={index}
					onClick={option.onClick}
					res={option.res}
				/>
			))}
		</MoreHorizMenu>
	);
});